<template>
	<div>
		<div class="container hidden-xs">
			<b-breadcrumbs :items="breadcrumbs"/>
		</div>
		<delete-modal
			:show-modal="showDeleteModal"
			:details="currentUser"
			@closeDeleteModal="hideDeleteModal"
			@delete="deleteUser"
		/>
		<section class="container">
			<div class="row">
				<div class="col-xs-12">
					<h1>Users</h1>
					<div class="page-summary-container">
						<div class="page-summary-text">
							Add, edit &amp; delete users
						</div>
					</div>
				</div>
			</div>
			<div class="row">
				<div class="col-xs-12 col-sm-6">
					<div class="input-group">
						<input
							v-model="searchTerm"
							type="text"
							class="form-control"
							style="height:40.25px;"
							placeholder="Search users"
							@keyup.enter="getUsers"
						>
						<span class="input-group-btn">
							<b-btn class="btn-primary" @click="getUsers">
								<i class="material-icons">search</i>
							</b-btn>
						</span>
					</div>
				</div>
				<div class="col-xs-6 col-sm-3">
					<b-btn
						class="btn-primary btn-md btn-block btn-filter-admin collapsed"
						data-toggle="collapse"
						aria-expanded="false"
						@click="showFilters=!showFilters"
					>
					Filter
					</b-btn>
				</div>
				<div class="col-xs-6 col-sm-3">
					<router-link
						to="/admin/users/new"
						class="btn btn-info btn-md btn-block btn-filter-admin"
						role="button"
					>
					Add new
					</router-link>
				</div>
			</div>
			<div class="row">
				<div class="col-xs-12">
					<filtering
						:showFilters="showFilters"
						:filter-options="filterOptions"
						:sort-options="sortOptions"
						:selected-filters="selectedFilters"
						@resetSearchTerm="setSearchTerm(null)"
						@selectedOptions="applyFilters"
					/>
				</div>
			</div>
			<template v-if="loading">
				<div class="row">
					<div class="col-xs-12">
						<div class="container loading-container">
							<img class="section-loader" src="@/assets/images/fse_reg-section-loader-admin.gif"/>
						</div>
					</div>
				</div>
			</template>
			<template v-else-if="users && users.length > 0">
				<div
					v-for="user in users"
					:key="user.id"
					class="list-container list-container-notifications"
				>
					<div class="row">
						<!--
						<div class="col-xs-12 col-sm-12 col-md-2 col-lg-2">
							<template v-if="user.regions.length > 0" >
								<div class="sm-padding-bottom-10">
									<b-btn
										v-for="region in user.regions"
										:key="region.id"
										class="label label-red"
									>
										&nbsp;{{ region.name }}&nbsp;
									</b-btn>
								</div>
							</template>
							<template v-else>
								<div class="sm-padding-bottom-10">
									<span class="label label-success">All regions available</span>
								</div>
							</template>
						</div>
						-->
						<div class="col-xs-12 col-sm-12 col-md-3 col-lg-3">
							<div class="sm-padding-bottom-10">
								<div class="notification-list-heading">
									<template v-if="user.title">
										{{ `${user.title}. ${user.first_name} ${user.last_name}` }}
									</template>
									<template v-else>
										{{ `${user.first_name} ${user.last_name}` }}
									</template>
								</div>
							</div>
						</div>
						<div class="col-xs-12 col-sm-12 col-md-2 col-lg-2">
							<span :class="user.is_admin ? 'label label-primary' : 'label label-default' ">
								{{ user.is_admin ? 'Admin' : 'User' }}
							</span>
						</div>
						<div class="col-xs-12 col-sm-12 col-md-4 col-lg-4">
							<div class="sm-padding-bottom-10">
								<div class="no-margin">{{ user.email_address }}</div>
							</div>
						</div>
						<div class="col-xs-12 col-sm-12 col-md-1 col-lg-1">
							<b-dropdown class="btn-group pull-right">
								<b-btn class="btn btn-primary btn-sm dropdown-toggle">
									Actions
									<span class="form-label sr-only">Toggle Dropdown</span>
								</b-btn>
								<template slot="dropdown">
									<li>
										<router-link
											:to="`/admin/users/${user.id}`"
											role="button"
										>Edit</router-link>
									</li>
									<template v-if="pagination.total > 1">
										<li>
											<a
												@click="showDeleteModal=true; openDeleteModal(user)"
												role="button"
											>Delete</a>
										</li>
									</template>
								</template>
							</b-dropdown>
						</div>
					</div>
				</div>
			</template>
			<template v-else>
				<div class="row">
					<div class="col-xs-12">
						<div class="container center">
							<h2>No results found</h2>
						</div>
					</div>
				</div>
			</template>
			<div class="row">
				<div class="col-xs-12">
					<div class="pagination-container">
						<b-pagination
							v-if="pagination && pagination.totalPages > 1"
							v-model="pagination.page"
							:total-page="pagination.totalPages"
						/>
					</div>
				</div>
			</div>
		</section>
	</div>
</template>

<style lang="less" scoped>
	.input-group {
		margin-bottom: 30px;
	}
	.btn-primary {
		height: 40.25px;
	}
	.loading-container {
		text-align: center;
	}
	.section-loader {
		max-width: 200px;
		height: auto;
		margin: 30px auto;
	}
</style>

<script>
import filtering from '@/components/filtering';
import deleteModal from '@/components/delete-modal';
import { map } from 'lodash/fp';
import { Notification } from 'uiv';
import { mapGetters } from 'vuex';
import { determineNextPageOnDelete } from '@/modules/utils';

export default {
	components: { filtering, deleteModal },
	created() {
		this.getUsers();
		this.getUserFilterOptions();
	},
	data() {
		return {
			showFilters: false,
			searchTerm: null,
			users: null,
			filterOptions: {},
			sortOptions: [],
			pagination: {
				page: 1
			},
			showDeleteModal: false,
			currentUser: {
				id: null,
				name: null
			},
			options: null,
			selectedSortOrder: null,
			selectedFilters: {
				regions: [],
			},
			loading: false
		};
	},
	computed: {
		...mapGetters({ getBreadcrumbs: 'breadcrumbs' }),
		breadcrumbs() {
			return this.getBreadcrumbs;
		}
	},
	methods: {
		openDeleteModal(user) {
			this.currentUser.id = user.id;
			this.currentUser.name = `${user.first_name} ${user.last_name}`;
		},
		hideDeleteModal() {
			this.showDeleteModal = false;
		},
		async getUserFilterOptions() {
			const { data: { data: options } } = await this.$axios.get('/user/filter-options');

			const formattedSortOptions = map(({ code, name }) => ({
				value: code, label: name
			}), options.sortOptions);

			const formattedRegions = map(({ id, name }) => ({
				value: id, label: name
			}), options.filterOptions.regions);

			this.$set(this, 'sortOptions', formattedSortOptions);
			this.$set(this, 'filterOptions', {
				regions: formattedRegions,
			});
		},
		async getUsers(params) {
			this.loading = true;
			const { searchTerm, selectedSortOrder, selectedFilters } = this;
			const { data: { data: users, pagination } } = await this.$axios.get('/user', {
				params: {
					searchTerm,
					sortBy: selectedSortOrder,
					page: this.pagination.page,
					...selectedFilters,
					...params
				}
			});
			this.$set(this, 'users', users);
			this.$set(this, 'pagination', pagination);
			this.loading = false;
		},
		async deleteUser(id) {
			await this.$axios.delete(`/user/${id}`);
			// if this is the last item in a page, render the previous page; current page limit is 20
			const page = determineNextPageOnDelete(this.pagination, 20);
			this.getUsers({ page });

			this.hideDeleteModal();
			Notification.notify({
				type: 'danger',
				title: 'User deleted',
				content: `Successfully deleted ${this.currentUser.name}.`,
				duration: 10000
			});
		},
		async applyFilters(options) {
			this.$set(this, 'selectedSortOrder', options.selectedSortOrder);
			this.$set(this, 'selectedFilters', options.selectedFilters);
			await this.getUsers({ page: 1 });
		},
		async setSearchTerm(term) {
			this.$set(this, 'searchTerm', term);
			await this.getUsers({ page: 1 });
		}
	},
	watch: {
		'pagination.page': async function pageWatcher() {
			await this.getUsers();
		}
	},
};
</script>
